/**
 * Table artifact template for dashboard module
 *
 * Delegates to TableArtifact with dashboard variant for single rendering path.
 * Currency comes from dashboard context (useResourceCurrency) via rendererProps.
 * DataGrid state persistence is passed when rendering in dashboard module.
 * Registers for dashboard download when rendered in a panel.
 */
import TableArtifact from "connected-components/ai-teammate/components/table-artifact"
import { useMemo, useCallback } from "react"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "connected-components/dashboard/analytics/events"
import { ARTIFACT_KINDS } from "connected-components/dashboard/modules/ai-artifact/types"
import { useRegisterDownloadData } from "hooks/use-register-download-data"
import { usePanelTitle } from "connected-components/dashboard/context/panel-id"
import { artifactToDownloadData } from "connected-components/ai-teammate/helpers/artifact-to-download-data"
import type { ArtifactTemplateProps } from "connected-components/dashboard/modules/ai-artifact/types"
import { DEFAULT_ARTIFACT_TITLES_BY_KIND } from "connected-components/dashboard/modules/ai-artifact/constants"

export default function TableTemplate({
  artifact,
  currency,
  dataGridState,
  onDataGridStateChange,
}: ArtifactTemplateProps) {
  const analytics = useAnalytics()
  const panelTitle = usePanelTitle()
  const downloadRegistration = useMemo(() => ({
    key: `ai-artifact-table-${artifact.id}`,
    sheetName: panelTitle ?? DEFAULT_ARTIFACT_TITLES_BY_KIND[ARTIFACT_KINDS.TABLE],
    getData: () => artifactToDownloadData(artifact, { excelCells: true }),
  }), [artifact, panelTitle])
  useRegisterDownloadData(downloadRegistration)
  const handleExplainOpen = useCallback(() => {
    analytics.track(DASHBOARD_EVENTS.AI_ARTIFACT_EXPLAIN_STEPS_OPENED, {
      artifactId: artifact.id,
      kind: ARTIFACT_KINDS.TABLE,
    })
  }, [analytics, artifact.id])

  if (artifact.type !== "table") {
    return null
  }

  return (
    <TableArtifact
      artifact={artifact}
      variant="dashboard"
      currency={currency}
      dataGridState={dataGridState}
      onDataGridStateChange={onDataGridStateChange}
      onExplainOpen={handleExplainOpen}
    />
  )
}
